"use client"

import { LeafletItem } from "@/components/leaflet/types"
import { calculateStatus, generateLeafletSlug } from "@/components/leaflet/utils"
import { t } from "@/languages/i18n"
import { DefaultImage } from "@/lib/external-components"
import { toImageSrc } from "@/lib/utils"
import { Letaks } from "@/types/codegen/Letaks"
import type { LetakListResponse } from "@/types/codegen/data-contracts"
import { useEffect, useState } from "react"

interface UseLeafletsOptions {
  locale: string
  initialItems?: LeafletItem[]
}

interface UseLeafletsReturn {
  leafletItems: LeafletItem[]
  loading: boolean
  error: string | null
}

export const useLeaflets = ({ locale, initialItems = [] }: UseLeafletsOptions): UseLeafletsReturn => {
  const [leafletItems, setLeafletItems] = useState<LeafletItem[]>(initialItems)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchLeaflets = async () => {
      try {
        setLoading(true)
        setError(null)

        const api = new Letaks()
        const res = await api.getLetaks(
          {
            populate: "*",
            sort: "createdAt:desc",
            "pagination[pageSize]": 100,
            locale,
          },
          { baseUrl: undefined },
        )

        const data = (res.data as LetakListResponse) || {}
        const items: LeafletItem[] =
          data.data?.map(item => {
            const attrs: any = item.attributes || {}

            let rawImage =
              attrs.featuredImage?.data?.attributes?.url || attrs.image?.data?.attributes?.url || attrs.image || ""

            const image = rawImage ? toImageSrc(rawImage) : DefaultImage

            const dateStart = attrs.dateStart
            const dateEnd = attrs.dateEnd
            const status = calculateStatus(dateStart, dateEnd)
            const id = item.id?.toString() || ""
            const title = attrs.title || attrs.name || t("Untitled")
            const slug = generateLeafletSlug(title, id)

            return {
              id,
              title,
              image,
              status,
              dateStart,
              dateEnd,
              slug,
              pages: attrs.pages || [],
              pdfFile: attrs.pdfFile || null,
              catalogImages: attrs.catalogImages || null,
            }
          }) || []

        setLeafletItems(items)
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : t("Lỗi khi tải danh sách letaky")
        setError(errorMessage)
        console.error("Lỗi khi tải letaky qua codegen API", err)
      } finally {
        setLoading(false)
      }
    }

    fetchLeaflets()
  }, [locale])

  return {
    leafletItems,
    loading,
    error,
  }
}
